import { Box, LoadingOverlay, Stack } from "@mantine/core";
import type React from "react";
import { Link } from "react-router";

import { HeoLogo } from "~/ui/components/HeoLogo/HeoLogo";
import { Layout, type LayoutProps } from "~/ui/layout/Layout";
import { useIsAuthenticated } from "~/ui/routes/RouteGuards/useIsAuthenticated";

import styles from "./PublicPageLayout.module.css";

export interface PublicPageLayoutProps
  extends Pick<LayoutProps, "hideNavbarLinks" | "hideUserMenuLinks" | "hideFeedbackButton"> {
  children: React.ReactNode;
}

/**
 * Shows the full `Layout` (header + navbar) when authenticated,
 * otherwise a minimal shell (only header with logo).
 * For pages reachable with or without a session (e.g. NotFoundPage, NoAccessPage).
 */
const PublicPageLayout: React.FC<PublicPageLayoutProps> = (props) => {
  const { children, hideNavbarLinks, hideUserMenuLinks, hideFeedbackButton } = props;

  const { isAuthenticated, isLoading } = useIsAuthenticated();

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (isAuthenticated) {
    return (
      <Layout
        hideNavbarLinks={hideNavbarLinks}
        hideUserMenuLinks={hideUserMenuLinks}
        hideFeedbackButton={hideFeedbackButton}
      >
        {children}
      </Layout>
    );
  }

  return (
    <Stack gap={0} h={"100dvh"}>
      <Box component="header" className={styles.header}>
        <Box component={Link} to="/" data-testid="headerLogoLink">
          <HeoLogo height={32} dataTestId="headerLogo" />
        </Box>
      </Box>

      <Box className={styles.content}>{children}</Box>
    </Stack>
  );
};

export { PublicPageLayout };
